import { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { useAuthStore } from "../store/authStore";
import { apiClient } from "../lib/api";
import {
  ChevronLeft,
  Package,
  TruckIcon,
  BarChart3,
  Settings,
  Plus,
  Edit,
  Trash2,
  Search,
  Download,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  XCircle,
  ArrowUpDown,
  Maximize,
  Tag,
  Layers,
  ShoppingCart,
  FileText,
  Users,
  MapPin,
  Box,
  CalendarIcon,
  TrendingUp,
  DollarSign,
  Eye,
  Warehouse as WarehouseIcon,
} from "lucide-react";
import toast from "react-hot-toast";

// Custom Components
import { ErrorBoundary } from "../components/ui/ErrorBoundary";
import { ConfirmDialog } from "../components/ui/ConfirmDialog";
import { DataTable, Column } from "../components/ui/DataTable";
import ExportDialog from "../components/export/ExportDialog";

// Types
import type {
  Product,
  Category,
  ProductVariant,
  ProductBundle,
  Stock,
  Supplier,
  PurchaseOrder,
  StockAlert,
  LotNumber,
  SerialNumber,
  InventoryAnalytics,
  Recipe,
  ModifierGroup,
  Manufacturer,
  PriceLevel,
  PromotionalPricing,
  VariantAttribute,
  WarehouseStock,
} from "../types/inventory";

type TabType =
  | "products"
  | "stock"
  | "warehouses"
  | "suppliers"
  | "purchase-orders"
  | "transfers"
  | "adjustments"
  | "transactions"
  | "analytics"
  | "traceability";

const ITEMS_PER_PAGE = 50;
const DEBOUNCE_DELAY = 300;

export default function InventoryPage() {
  const { isAuthenticated, token } = useAuthStore();
  const [activeTab, setActiveTab] = useState<TabType>("products");
  const [activeSubTab, setActiveSubTab] = useState<any>("master");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmDialogProps, setConfirmDialogProps] = useState<any>({});

  // Products Data
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [variants, setVariants] = useState<ProductVariant[]>([]);
  const [bundles, setBundles] = useState<ProductBundle[]>([]);
  const [priceLevels, setPriceLevels] = useState<PriceLevel[]>([]);
  const [promotionalPricing, setPromotionalPricing] = useState<
    PromotionalPricing[]
  >([]);
  const [priceChangesLog, setPriceChangesLog] = useState<any[]>([]);
  const [variantAttributes, setVariantAttributes] = useState<
    VariantAttribute[]
  >([]);
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [modifierGroups, setModifierGroups] = useState<ModifierGroup[]>([]);
  const [manufacturers, setManufacturers] = useState<Manufacturer[]>([]);

  // Stock Data
  const [stock, setStock] = useState<Stock[]>([]);
  const [warehouseStock, setWarehouseStock] = useState<WarehouseStock[]>([]);
  const [stockAlerts, setStockAlerts] = useState<StockAlert[]>([]);
  const [stockMovements, setStockMovements] = useState<any[]>([]);

  // Warehouse Data
  const [warehouses, setWarehouses] = useState<any[]>([]);
  const [stores, setStores] = useState<any[]>([]);

  // Supply Chain Data
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);

  // Transfers & Movements
  const [transfers, setTransfers] = useState<any[]>([]);
  const [transferOrders, setTransferOrders] = useState<any[]>([]);
  const [adjustments, setAdjustments] = useState<any[]>([]);
  const [transactions, setTransactions] = useState<any[]>([]);

  // Traceability
  const [lotNumbers, setLotNumbers] = useState<LotNumber[]>([]);
  const [expiringItems, setExpiringItems] = useState<any[]>([]);
  const [serialNumbers, setSerialNumbers] = useState<SerialNumber[]>([]);

  // Analytics Data
  const [analytics, setAnalytics] = useState<InventoryAnalytics>({});

  // UI State
  const [searchTerm, setSearchTerm] = useState("");
  const [filterOptions, setFilterOptions] = useState<any>({
    category: "",
    status: "",
  });
  const [isViewMode, setIsViewMode] = useState(false);
  const [viewingProduct, setViewingProduct] = useState<Product | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);

  // Debounced search - trigger on any search term change (including empty)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (isAuthenticated && token) {
        fetchData();
      }
    }, DEBOUNCE_DELAY);
    return () => clearTimeout(timer);
  }, [searchTerm, isAuthenticated, token]);

  // Fetch data on tab/subtab change
  useEffect(() => {
    if (isAuthenticated && token) {
      fetchData();
    }
  }, [activeTab, activeSubTab, currentPage, isAuthenticated, token]);

  const handleFilterChange = useCallback((filters: Record<string, string>) => {
    setFilterOptions(filters);
    setCurrentPage(1);
  }, []);

  /**
   * Main data fetching orchestrator with error handling
   */
  const fetchData = useCallback(async () => {
    if (!isAuthenticated || !token) {
      console.log("Not authenticated, skipping fetch");
      return;
    }
    setLoading(true);
    setError(null);

    try {
      switch (activeTab) {
        case "products":
          await fetchProductsData();
          break;
        case "stock":
          await fetchStockData();
          break;
        case "warehouses":
          await fetchWarehouseData();
          break;
        case "suppliers":
          await fetchSupplierData();
          break;
        case "purchase-orders":
          await fetchPurchaseOrderData();
          break;
        case "transfers":
          await fetchTransferData();
          break;
        case "adjustments":
          await fetchAdjustmentData();
          break;
        case "transactions":
          await fetchTransactionData();
          break;
        case "analytics":
          await fetchAnalyticsData();
          break;
        case "traceability":
          await fetchTraceabilityData();
          break;
      }
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error || err.message || "Failed to load data";
      setError(errorMessage);
      toast.error(errorMessage);
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  }, [activeTab, activeSubTab, currentPage, searchTerm, filterOptions, isAuthenticated, token]);

  const fetchProductsData = useCallback(async () => {
    const params = new URLSearchParams({
      page: currentPage.toString(),
      limit: ITEMS_PER_PAGE.toString(),
      search: searchTerm,
      ...filterOptions,
    });

    try {
      if (activeSubTab === "master") {
        const response = await apiClient.get(`/api/inventory/products?${params}`);
        const data = response.data.data || response.data;
        setProducts(Array.isArray(data) ? data : []);
        setTotalCount(
          response.data.total || (Array.isArray(data) ? data.length : 0),
        );
      } else if (activeSubTab === "variants") {
        const response = await apiClient.get(`/api/inventory/variants?${params}`);
        const data = response.data.data || response.data;
        setVariants(Array.isArray(data) ? data : []);
        setTotalCount(
          response.data.total || (Array.isArray(data) ? data.length : 0),
        );
      } else if (activeSubTab === "bundles") {
        const response = await apiClient.get(`/api/inventory/bundles?${params}`);
        const data = response.data.data || response.data;
        setBundles(Array.isArray(data) ? data : []);
        setTotalCount(
          response.data.total || (Array.isArray(data) ? data.length : 0),
        );
      } else if (activeSubTab === "categories") {
        const response = await apiClient.get(`/api/inventory/categories?${params}`);
        const data = response.data.data || response.data;
        setCategories(Array.isArray(data) ? data : []);
        setTotalCount(
          response.data.total || (Array.isArray(data) ? data.length : 0),
        );
      } else if (activeSubTab === "pricing") {
        const [levelsRes, promoRes, historyRes] = await Promise.all([
          apiClient.get("/api/inventory/price-levels"),
          apiClient.get("/api/inventory/promotions"),
          apiClient.get(`/api/inventory/price-history?${params}`),
        ]);
        setPriceLevels(levelsRes.data || []);
        setPromotionalPricing(promoRes.data || []);
        const historyData = historyRes.data?.data || historyRes.data || [];
        setPriceChangesLog(historyData);
        setTotalCount(historyRes.data?.total || historyData.length || 0);
      } else if (activeSubTab === "attributes") {
        const [attrRes, recipeRes, modRes, mfgRes] = await Promise.all([
          apiClient.get("/api/inventory/attributes"),
          apiClient.get("/api/inventory/recipes"),
          apiClient.get("/api/inventory/modifier-groups"),
          apiClient.get("/api/inventory/manufacturers"),
        ]);
        setVariantAttributes(attrRes.data || []);
        setRecipes(recipeRes.data || []);
        setModifierGroups(modRes.data || []);
        setManufacturers(mfgRes.data || []);
        setTotalCount(attrRes.data?.length || 0);
      }
    } catch (err: any) {
      console.error("Error fetching products data:", err);
    }
  }, [activeSubTab, currentPage, searchTerm, filterOptions]);

  /**
   * Enhanced Stock Data with warehouse stock
   */
  const fetchStockData = async () => {
    const params = new URLSearchParams({
      page: currentPage.toString(),
      limit: ITEMS_PER_PAGE.toString(),
      search: searchTerm,
      ...filterOptions,
    });

    try {
      if (activeSubTab === "levels") {
        const response = await apiClient.get(`/api/inventory/stock?${params}`);
        const data = response.data.data || response.data;
        setStock(Array.isArray(data) ? data : []);
        setTotalCount(
          response.data.total || (Array.isArray(data) ? data.length : 0),
        );
      } else if (activeSubTab === "alerts") {
        const response = await apiClient.get(`/api/inventory/stock-alerts?${params}`);
        const data = response.data.data || response.data;
        setStockAlerts(Array.isArray(data) ? data : []);
        setTotalCount(
          response.data.total || (Array.isArray(data) ? data.length : 0),
        );
      } else if (activeSubTab === "movements") {
        const response = await apiClient.get(
          `/api/inventory/transactions?${params}`,
        );
        const data = response.data.data || response.data;
        setStockMovements(Array.isArray(data) ? data : []);
        setTotalCount(
          response.data.total || (Array.isArray(data) ? data.length : 0),
        );
      }
    } catch (err) {
      console.error("Error fetching stock data:", err);
    }
  };

  /**
   * Warehouse Data with stock levels
   */
  const fetchWarehouseData = async () => {
    const params = new URLSearchParams({
      page: currentPage.toString(),
      limit: ITEMS_PER_PAGE.toString(),
      search: searchTerm,
    });

    try {
      if (activeSubTab === "locations") {
        const [whRes, storeRes] = await Promise.all([
          apiClient.get(`/api/inventory/warehouses?${params}`),
          apiClient.get(`/api/inventory/stores?${params}`),
        ]);
        setWarehouses(whRes.data.data || whRes.data);
        setStores(storeRes.data.data || storeRes.data);
      } else if (activeSubTab === "stock") {
        const response = await apiClient.get(`/api/inventory/warehouse-stock?${params}`);
        const data = response.data.data || response.data;
        setWarehouseStock(Array.isArray(data) ? data : []);
        setTotalCount(
          response.data.total || (Array.isArray(data) ? data.length : 0),
        );
      }
    } catch (err) {
      console.error("Error fetching warehouse data:", err);
    }
  };

  /**
   * Traceability with expiry tracking
   */
  const fetchTraceabilityData = async () => {
    const params = new URLSearchParams({
      page: currentPage.toString(),
      limit: ITEMS_PER_PAGE.toString(),
      search: searchTerm,
    });

    try {
      if (activeSubTab === "lots") {
        const response = await apiClient.get(
          `/api/inventory/lots?${params}`,
        );
        const data = response.data.data || response.data;
        setLotNumbers(Array.isArray(data) ? data : []);
        setTotalCount(
          response.data.total || (Array.isArray(data) ? data.length : 0),
        );
      } else if (activeSubTab === "serials") {
        const response = await apiClient.get(
          `/api/inventory/serials?${params}`,
        );
        const data = response.data.data || response.data;
        setSerialNumbers(Array.isArray(data) ? data : []);
        setTotalCount(
          response.data.total || (Array.isArray(data) ? data.length : 0),
        );
      } else if (activeSubTab === "expiry") {
        const response = await apiClient.get(
          `/api/inventory/lots?expiringSoon=true&${params}`,
        );
        const data = response.data.data || response.data;
        setExpiringItems(Array.isArray(data) ? data : []);
        setTotalCount(
          response.data.total || (Array.isArray(data) ? data.length : 0),
        );
      }
    } catch (err) {
      console.error("Error fetching traceability data:", err);
    }
  };

  /**
   * Analytics with materialized views
   */
  const fetchAnalyticsData = async () => {
    const response = await apiClient.get("/api/inventory/analytics");
    setAnalytics(response.data);
  };

  const fetchSupplierData = async () => {
    const params = new URLSearchParams({
      page: currentPage.toString(),
      limit: ITEMS_PER_PAGE.toString(),
      search: searchTerm,
    });

    try {
      if (activeSubTab === "vendors") {
        const response = await apiClient.get(`/api/inventory/suppliers?${params}`);
        const data = response.data.data || response.data;
        setSuppliers(Array.isArray(data) ? data : []);
        setTotalCount(
          response.data.total || (Array.isArray(data) ? data.length : 0),
        );
      }
    } catch (err) {
      console.error("Error fetching supplier data:", err);
    }
  };

  const fetchPurchaseOrderData = async () => {
    const params = new URLSearchParams({
      page: currentPage.toString(),
      limit: ITEMS_PER_PAGE.toString(),
      search: searchTerm,
    });
    if (activeSubTab !== "history") {
      params.append(
        "status",
        activeSubTab === "active" ? "approved" : activeSubTab,
      );
    }
    try {
      const response = await apiClient.get(`/api/inventory/purchase-orders?${params}`);
      const data = response.data.data || response.data;
      setPurchaseOrders(Array.isArray(data) ? data : []);
      setTotalCount(
        response.data.total || (Array.isArray(data) ? data.length : 0),
      );
    } catch (err) {
      console.error("Error fetching purchase orders:", err);
    }
  };

  const fetchTransferData = async () => {
    const params = new URLSearchParams({
      page: currentPage.toString(),
      limit: ITEMS_PER_PAGE.toString(),
      search: searchTerm,
    });

    try {
      if (activeSubTab === "inter-warehouse") {
        const response = await apiClient.get(
          `/api/inventory/warehouse-transfers?${params}`,
        );
        const data = response.data.data || response.data;
        setTransferOrders(Array.isArray(data) ? data : []);
        setTotalCount(
          response.data.total || (Array.isArray(data) ? data.length : 0),
        );
      } else {
        if (activeSubTab !== "completed") {
          params.append(
            "status",
            activeSubTab === "pending" ? "draft" : "in_transit",
          );
        }
        const response = await apiClient.get(
          `/api/inventory/warehouse-transfers?${params}`,
        );
        const data = response.data.data || response.data;
        setTransfers(Array.isArray(data) ? data : []);
        setTotalCount(
          response.data.total || (Array.isArray(data) ? data.length : 0),
        );
      }
    } catch (err) {
      console.error("Error fetching transfers:", err);
    }
  };

  const fetchAdjustmentData = async () => {
    const params = new URLSearchParams({
      page: currentPage.toString(),
      limit: ITEMS_PER_PAGE.toString(),
      search: searchTerm,
    });
    if (activeSubTab !== "history") {
      params.append("status", activeSubTab);
    }
    try {
      const response = await apiClient.get(`/api/inventory/adjustments?${params}`);
      const data = response.data.data || response.data;
      setAdjustments(Array.isArray(data) ? data : []);
      setTotalCount(
        response.data.total || (Array.isArray(data) ? data.length : 0),
      );
    } catch (err) {
      console.error("Error fetching adjustments:", err);
    }
  };

  const fetchTransactionData = async () => {
    const params = new URLSearchParams({
      page: currentPage.toString(),
      limit: ITEMS_PER_PAGE.toString(),
      search: searchTerm,
    });
    if (activeSubTab !== "all") {
      params.append("type", activeSubTab);
    }
    try {
      const response = await apiClient.get(
        `/api/inventory/transactions?${params}`,
      );
      const data = response.data.data || response.data;
      setTransactions(Array.isArray(data) ? data : []);
      setTotalCount(
        response.data.total || (Array.isArray(data) ? data.length : 0),
      );
    } catch (err) {
      console.error("Error fetching transactions:", err);
    }
  };

  const handleMaximize = () => {
    setIsFullscreen(!isFullscreen);
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  const openConfirmDialog = (props: any) => {
    setConfirmDialogProps(props);
    setShowConfirmDialog(true);
  };

  const closeConfirmDialog = () => {
    setShowConfirmDialog(false);
    setConfirmDialogProps({});
  };

  const confirmAction = () => {
    if (confirmDialogProps.onConfirm) {
      confirmDialogProps.onConfirm();
    }
    closeConfirmDialog();
  };

  /**
   * Enhanced Product Columns with all fields
   */
  const [isProductFormOpen, setIsProductFormOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [formData, setFormData] = useState({
    code: "",
    name: "",
    category_id: "",
    manufacturer_name: "",
    barcode: "",
    cost_price: "",
    price: "",
    minimum_stock: "",
    reorder_level: "",
    description: "",
    is_active: true,
    unit_of_measure: "pcs",
    tax_rate: "0",
    track_inventory: true,
    is_service: false,
    allow_backorder: false,
    reorder_quantity: "0",
    manufacturer_id: "",
    model_number: "",
    tax_category: "",
    weight: "",
    weight_unit: "kg",
    volume: "",
    volume_unit: "L",
    dimensions: "",
    product_tags: "",
    meta_title: "",
    meta_description: "",
    meta_keywords: "",
    warranty_months: 0,
    return_days: 0,
    shelf_life_days: 0,
    storage_instructions: "",
    preparation_instructions: "",
    default_supplier_id: "",
    color: "",
    is_featured: false,
  });
  const [isSaving, setIsSaving] = useState(false);

  // Category Form State
  const [isCategoryFormOpen, setIsCategoryFormOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [categoryFormData, setCategoryFormData] = useState({
    name: "",
    color: "",
    description: "",
    is_active: true,
    image: "",
    parent_category_id: "",
  });

  // Variant Form State
  const [isVariantFormOpen, setIsVariantFormOpen] = useState(false);
  const [editingVariant, setEditingVariant] = useState<ProductVariant | null>(
    null,
  );
  const [variantFormData, setVariantFormData] = useState({
    product_id: "",
    name: "",
    sku: "",
    sale_price: "",
    cost_price: "",
    barcode: "",
    is_active: true,
    sort_order: 0,
  });

  // Bundle Form State
  const [isBundleFormOpen, setIsBundleFormOpen] = useState(false);
  const [editingBundle, setEditingBundle] = useState<ProductBundle | null>(
    null,
  );
  const [bundleFormData, setBundleFormData] = useState({
    product_id: "",
    name: "",
    description: "",
    pricing_type: "fixed" as any,
    bundle_price: "",
    discount_percentage: "",
    is_active: true,
  });


  const handleViewProduct = (product: Product) => {
    setViewingProduct(product);
    setIsViewMode(true);
  };

  const handleEditProduct = (product: Product) => {
    setEditingProduct(product);
    setIsViewMode(false);
    setFormData({
      code: product.code || "",
      name: product.name || "",
      category_id: product.category_id || "",
      manufacturer_name: product.manufacturer || "",
      barcode: product.barcode || "",
      cost_price: product.cost_price?.toString() || "",
      price: product.price?.toString() || "",
      minimum_stock: product.minimum_stock?.toString() || "",
      reorder_level: product.reorder_level?.toString() || "",
      description: product.description || "",
      is_active: product.is_active !== false,
      unit_of_measure: product.unit_of_measure || "pcs",
      tax_rate: (product.tax_rate || "0").toString(),
      track_inventory: product.track_inventory ?? true,
      is_service: product.is_service ?? false,
      allow_backorder: product.allow_backorder ?? false,
      reorder_quantity: (product.reorder_quantity || "0").toString(),
      manufacturer_id: product.manufacturer_id || "",
      model_number: product.model_number || "",
      tax_category: product.tax_category || "",
      weight: (product.weight || "").toString(),
      weight_unit: product.weight_unit || "kg",
      volume: (product.volume || "").toString(),
      volume_unit: product.volume_unit || "L",
      dimensions: product.dimensions || "",
      product_tags: Array.isArray(product.product_tags) ? product.product_tags.join(", ") : (product.product_tags || ""),
      meta_title: product.meta_title || "",
      meta_description: product.meta_description || "",
      meta_keywords: product.meta_keywords || "",
      warranty_months: product.warranty_months || 0,
      return_days: product.return_days || 0,
      shelf_life_days: product.shelf_life_days || 0,
      storage_instructions: product.storage_instructions || "",
      preparation_instructions: product.preparation_instructions || "",
      default_supplier_id: product.default_supplier_id || "",
      color: product.color || "",
      is_featured: product.is_featured || false,
    });
    setIsProductFormOpen(true);
  };

  const handleEditBundle = (bundle: ProductBundle) => {
    setEditingBundle(bundle);
    setBundleFormData({
      product_id: bundle.id || "",
      name: bundle.name || "",
      description: bundle.description || "",
      pricing_type: bundle.pricing_type || "fixed",
      bundle_price: bundle.bundle_price?.toString() || "",
      discount_percentage: bundle.discount_percentage?.toString() || "",
      is_active: bundle.is_active !== false,
    });
    setIsBundleFormOpen(true);
  };

  const handleEditCategory = (category: Category) => {
    setEditingCategory(category);
    setCategoryFormData({
      name: category.name || "",
      color: category.color || "",
      description: category.description || "",
      is_active: category.is_active !== false,
      image: category.image || "",
      parent_category_id: category.parent_category_id || "",
    });
    setIsCategoryFormOpen(true);
  };

  const handleNewVariant = () => {
    setEditingVariant(null);
    setVariantFormData({
      product_id: "",
      name: "",
      sku: "",
      sale_price: "",
      cost_price: "",
      barcode: "",
      is_active: true,
      sort_order: 0,
    });
    setIsVariantFormOpen(true);
  };

  const handleNewBundle = () => {
    setEditingBundle(null);
    setBundleFormData({
      product_id: "",
      name: "",
      description: "",
      pricing_type: "fixed",
      bundle_price: "",
      discount_percentage: "",
      is_active: true,
    });
    setIsBundleFormOpen(true);
  };

  const handleNewCategory = () => {
    setEditingCategory(null);
    setCategoryFormData({
      name: "",
      color: "",
      description: "",
      is_active: true,
      image: "",
      parent_category_id: "",
    });
    setIsCategoryFormOpen(true);
  };


  const handleNewProduct = () => {
    setEditingProduct(null);
    setIsViewMode(false);
    setFormData({
      code: "",
      name: "",
      category_id: "",
      manufacturer_name: "",
      barcode: "",
      cost_price: "",
      price: "",
      minimum_stock: "",
      reorder_level: "",
      description: "",
      is_active: true,
      unit_of_measure: "pcs",
      tax_rate: "0",
      track_inventory: true,
      is_service: false,
      allow_backorder: false,
      reorder_quantity: "0",
      manufacturer_id: "",
      model_number: "",
      tax_category: "",
      weight: "",
      weight_unit: "kg",
      volume: "",
      volume_unit: "L",
      dimensions: "",
      product_tags: "",
      meta_title: "",
      meta_description: "",
      meta_keywords: "",
      warranty_months: 0,
      return_days: 0,
      shelf_life_days: 0,
      storage_instructions: "",
      preparation_instructions: "",
      default_supplier_id: "",
      color: "",
      is_featured: false,
    });
    setIsProductFormOpen(true);
  };


  const handleFormInputChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveProduct = async () => {
    if (!formData.code.trim() || !formData.name.trim() || !formData.price) {
      toast.error("Please fill in required fields: SKU, Name, and Price");
      return;
    }

    const payload = {
      ...formData,
      price: parseFloat(formData.price || "0"),
      cost_price: parseFloat(formData.cost_price || "0"),
      minimum_stock: parseFloat(formData.minimum_stock || "0"),
      reorder_level: parseFloat(formData.reorder_level || "0"),
      tax_rate: parseFloat(formData.tax_rate || "0"),
      reorder_quantity: parseFloat(formData.reorder_quantity || "0"),
      weight: formData.weight ? parseFloat(formData.weight) : null,
      volume: formData.volume ? parseFloat(formData.volume) : null,
      product_tags: formData.product_tags ? String(formData.product_tags).split(",").map(s => s.trim()) : [],
      warranty_months: parseInt(formData.warranty_months.toString()) || 0,
      return_days: parseInt(formData.return_days.toString()) || 0,
      shelf_life_days: parseInt(formData.shelf_life_days.toString()) || 0,
    };

    setIsSaving(true);
    try {
      if (editingProduct) {
        await apiClient.put(`/api/inventory/products/${editingProduct.id}`, payload);
        toast.success(`Product "${formData.name}" updated successfully`);
      } else {
        await apiClient.post("/api/inventory/products", payload);
        toast.success(`Product "${formData.name}" created successfully`);
      }

      setIsProductFormOpen(false);
      setEditingProduct(null);
      await fetchData();
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.error ||
        error.message ||
        "Failed to save product";
      toast.error(errorMsg);
      console.error("Error saving product:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleExportProducts = async () => {
    try {
      // @ts-ignore
      const pdfMake = (await import("pdfmake/build/pdfmake")).default;
      // @ts-ignore
      const pdfFonts = (await import("pdfmake/build/vfs_fonts")).default;
      pdfMake.vfs = pdfFonts.vfs;

      const body = [
        ["SKU", "Name", "Category", "Price", "Stock", "Status"].map((h) => ({
          text: h,
          style: "tableHeader",
        })),
      ];

      products.forEach((p) => {
        body.push([
          { text: p.code || "", style: "tableBody" },
          { text: p.name || "", style: "tableBody" },
          { text: p.category?.name || "", style: "tableBody" },
          { text: `$${parseFloat(p.price?.toString() || "0").toFixed(2)}`, style: "tableBody" },
          { text: p.current_stock?.toString() || "0", style: "tableBody" },
          { text: p.is_active ? "Active" : "Inactive", style: "tableBody" },
        ]);
      });

      const docDefinition = {
        background: [
          {
            image: "logo",
            width: 1200,
            opacity: 0.04,
            absolutePosition: { x: -300, y: 80 },
          },
        ],
        images: {
          logo: `${window.location.origin}/auth-bg.png`,
        },
        content: [
          {
            columns: [
              { text: "Product Catalog", style: "header" },
              {
                text: `Generated: ${new Date().toLocaleDateString()}`,
                alignment: "right",
                style: "subheader",
              },
            ],
          },
          {
            table: {
              headerRows: 1,
              widths: ["auto", "*", "auto", "auto", "auto", "auto"],
              body: body,
            },
            layout: {
              fillColor: function (rowIndex: number) {
                return rowIndex % 2 === 0 ? "#f9fafb" : null;
              },
              hLineColor: "#eeeeee",
              vLineColor: "#eeeeee",
              hLineWidth: function () {
                return 0.5;
              },
              vLineWidth: function () {
                return 0.5;
              },
            },
          },
        ],
        styles: {
          header: {
            fontSize: 28,
            bold: true,
            color: "#333333",
            margin: [0, 0, 0, 20],
          },
          subheader: { fontSize: 10, color: "#999999", margin: [0, 10, 0, 0] },
          tableHeader: {
            bold: true,
            fontSize: 12,
            color: "#333333",
            fillColor: "#f0f0f0",
            margin: [5, 5, 5, 5],
          },
          tableBody: {
            fontSize: 10,
            margin: [5, 5, 5, 5],
          },
        },
      };

      // @ts-ignore
      pdfMake.createPdf(docDefinition).download("products_catalog.pdf");
      toast.success("Professional PDF Catalog generated");
    } catch (error) {
      console.error("PDF Export Error:", error);
      toast.error("Failed to export PDF");
    }
  };

  const categoryColumns: Column<Category>[] = useMemo(
    () => [
      {
        key: "name",
        header: "Name",
        accessor: (row) => row.name,
        sortable: true,
        filterable: true,
      },
      {
        key: "description",
        header: "Description",
        accessor: (row) => row.description || "-",
      },
      {
        key: "is_active",
        header: "Status",
        accessor: (row) => (row.is_active ? "Active" : "Inactive"),
        render: (value) => (
          <span
            className={`px-2 py-0.5 rounded-full text-xs ${
              value === "Active"
                ? "bg-green-900/20 text-green-400"
                : "bg-red-900/20 text-red-400"
            }`}
          >
            {value}
          </span>
        ),
      },
    ],
    [],
  );

  const variantColumns: Column<ProductVariant>[] = useMemo(
    () => [
      {
        key: "sku",
        header: "SKU",
        accessor: (row) => row.sku,
        sortable: true,
        filterable: true,
      },
      {
        key: "name",
        header: "Name",
        accessor: (row) => row.name,
        sortable: true,
        filterable: true,
      },
      {
        key: "price",
        header: "Price",
        accessor: (row) => row.price,
        render: (value) => `$${parseFloat(value || 0).toFixed(2)}`,
        align: "right",
      },
      {
        key: "cost_price",
        header: "Cost",
        accessor: (row) => row.cost_price,
        render: (value) => `$${parseFloat(value || 0).toFixed(2)}`,
        align: "right",
      },
      {
        key: "barcode",
        header: "Barcode",
        accessor: (row) => row.barcode || "-",
      },
    ],
    [],
  );

  const bundleColumns: Column<ProductBundle>[] = useMemo(
    () => [
      {
        key: "name",
        header: "Bundle Name",
        accessor: (row) => row.name,
        sortable: true,
        filterable: true,
      },
      {
        key: "bundle_price",
        header: "Price",
        accessor: (row) => row.bundle_price,
        render: (value) => `$${parseFloat(value || 0).toFixed(2)}`,
        align: "right",
      },
    ],
    [],
  );

  const productColumns: Column<Product>[] = useMemo(
    () => [
      {
        key: "code",
        header: "SKU",
        accessor: (row) => row.code,
        sortable: true,
        filterable: true,
        width: "120px",
      },
    {
      key: "name",
      header: "Product Name",
      accessor: (row) => row.name,
      sortable: true,
      filterable: true,
      render: (value, row) => (
        <div>
          <div className="text-white font-medium">{value}</div>
          <div className="text-xs text-gray-500 flex gap-2">
            {row.barcode && <span>Barcode: {row.barcode}</span>}
            {row.manufacturer && <span>Manufacturer: {row.manufacturer}</span>}
          </div>
        </div>
      ),
    },
      {
        key: "category",
        header: "Category",
        accessor: (row) => row.category?.name || "-",
        sortable: true,
        filterable: true,
      },
      {
        key: "manufacturer",
        header: "Manufacturer",
        accessor: (row) => row.manufacturer || "-",
        sortable: true,
        filterable: true,
      },
      {
        key: "cost_price",
        header: "Cost",
        accessor: (row) => row.cost_price,
        align: "right",
        render: (value) => `$${parseFloat(value || 0).toFixed(2)}`,
        sortable: true,
      },
      {
        key: "price",
        header: "Price",
        accessor: (row) => row.price,
        align: "right",
        render: (value) => `$${parseFloat(value || 0).toFixed(2)}`,
        sortable: true,
        className: "text-white font-semibold",
      },
      {
        key: "current_stock",
        header: "Stock",
        accessor: (row) => row.current_stock || 0,
        align: "right",
        render: (value, row) => (
          <div>
            <span
              className={`${
                (value as number) <= (row.minimum_stock || 0)
                  ? "text-red-400 font-bold"
                  : (value as number) <= (row.minimum_stock || 0) * 1.5
                    ? "text-yellow-400"
                    : "text-green-400"
              }`}
            >
              {value}
            </span>
            {row.reorder_level && (
              <div className="text-xs text-gray-500">
                Reorder: {row.reorder_level}
              </div>
            )}
          </div>
        ),
        sortable: true,
      },
    ],
    [],
  );

  // View Mode Modal
  const renderViewProductModal = () => {
    if (!isViewMode || !viewingProduct) return null;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
        <div className="bg-gray-800 rounded-lg w-full max-w-2xl shadow-xl border border-gray-700 max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-gray-800 border-b border-gray-700 px-6 py-4 flex justify-between items-center">
            <h3 className="text-xl font-bold text-white">Product Details</h3>
            <button
              onClick={() => {
                setIsViewMode(false);
                setViewingProduct(null);
              }}
              className="text-gray-400 hover:text-white"
            >
              ✕
            </button>
          </div>

          <div className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="text-sm text-gray-400">SKU Code</label>
                <p className="text-white font-medium mt-1">
                  {viewingProduct.sku}
                </p>
              </div>
              <div>
                <label className="text-sm text-gray-400">Product Name</label>
                <p className="text-white font-medium mt-1">
                  {viewingProduct.name}
                </p>
              </div>
              <div>
                <label className="text-sm text-gray-400">Brand</label>
                <p className="text-white mt-1">
                  {viewingProduct.brand || "-"}
                </p>
              </div>
              <div>
                <label className="text-sm text-gray-400">Category</label>
                <p className="text-white mt-1">
                  {viewingProduct.category?.name || "-"}
                </p>
              </div>
              <div>
                <label className="text-sm text-gray-400">Barcode</label>
                <p className="text-white font-mono mt-1">
                  {viewingProduct.barcode || "-"}
                </p>
              </div>
              <div>
                <label className="text-sm text-gray-400">Cost Price</label>
                <p className="text-white font-medium mt-1">
                  $
                  {parseFloat(
                    viewingProduct.cost_price?.toString() || "0",
                  ).toFixed(2)}
                </p>
              </div>
              <div>
                <label className="text-sm text-gray-400">Selling Price</label>
                <p className="text-white font-bold text-lg mt-1">
                  $
                  {parseFloat(viewingProduct.price?.toString() || "0").toFixed(
                    2,
                  )}
                </p>
              </div>
              <div>
                <label className="text-sm text-gray-400">Minimum Stock</label>
                <p className="text-white mt-1">
                  {viewingProduct.minimum_stock || 0}
                </p>
              </div>
              <div>
                <label className="text-sm text-gray-400">Reorder Level</label>
                <p className="text-white mt-1">
                  {viewingProduct.reorder_level || 0}
                </p>
              </div>
              <div>
                <label className="text-sm text-gray-400">Status</label>
                <p className="mt-1">
                  <span
                    className={`px-2 py-1 text-xs rounded-full ${
                      viewingProduct.is_active
                        ? "bg-green-900 text-green-200"
                        : "bg-gray-700 text-gray-400"
                    }`}
                  >
                    {viewingProduct.is_active ? "Active" : "Inactive"}
                  </span>
                </p>
              </div>
              {viewingProduct.description && (
                <div className="col-span-2">
                  <label className="text-sm text-gray-400">Description</label>
                  <p className="text-white mt-1">
                    {viewingProduct.description}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="bg-gray-750 border-t border-gray-700 px-6 py-4 flex justify-end gap-2">
            <button
              onClick={() => {
                setIsViewMode(false);
                setViewingProduct(null);
              }}
              className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-500 transition-colors"
            >
              Close
            </button>
            <button
              onClick={() => {
                setIsViewMode(false);
                handleEditProduct(viewingProduct);
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-500 transition-colors"
            >
              Edit
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Production-Ready CRUD Form implementation
  const renderProductForm = () => {
    if (!isProductFormOpen) return null;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
        <div className="bg-gray-800 rounded-lg w-full max-w-4xl shadow-xl border border-gray-700 max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-gray-800 border-b border-gray-700 px-6 py-4 flex justify-between items-center">
            <h3 className="text-xl font-bold text-white">
              {editingProduct ? "Edit Product" : "Create New Product"}
            </h3>
            <button
              onClick={() => {
                setIsProductFormOpen(false);
                setEditingProduct(null);
              }}
              className="text-gray-400 hover:text-white"
            >
              ✕
            </button>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-2 gap-6 mb-6">
              {/* SKU Code - Required */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  SKU Code *
                </label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) =>
                    handleFormInputChange("code", e.target.value)
                  }
                  placeholder="e.g., PROD-001"
                  className="w-full bg-gray-700 border border-gray-600 rounded p-2 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Product Name - Required */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Product Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    handleFormInputChange("name", e.target.value)
                  }
                  placeholder="e.g., Widget Pro"
                  className="w-full bg-gray-700 border border-gray-600 rounded p-2 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Brand */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Brand
                </label>
                <input
                  type="text"
                  value={formData.manufacturer_name}
                  onChange={(e) =>
                    handleFormInputChange("manufacturer_name", e.target.value)
                  }
                  placeholder="e.g., Brand Name"
                  className="w-full bg-gray-700 border border-gray-600 rounded p-2 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Barcode */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Barcode
                </label>
                <input
                  type="text"
                  value={formData.barcode}
                  onChange={(e) =>
                    handleFormInputChange("barcode", e.target.value)
                  }
                  placeholder="e.g., 1234567890"
                  className="w-full bg-gray-700 border border-gray-600 rounded p-2 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Category *
                </label>
                <select
                  value={formData.category_id || ""}
                  onChange={(e) =>
                    handleFormInputChange("category_id", e.target.value)
                  }
                  className="w-full bg-gray-700 border border-gray-600 rounded p-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Category</option>
                  {Array.isArray(categories) && categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Cost Price */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Cost Price
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.cost_price}
                  onChange={(e) =>
                    handleFormInputChange("cost_price", e.target.value)
                  }
                  placeholder="0.00"
                  className="w-full bg-gray-700 border border-gray-600 rounded p-2 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Selling Price - Required */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Selling Price *
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) =>
                    handleFormInputChange("price", e.target.value)
                  }
                  placeholder="0.00"
                  className="w-full bg-gray-700 border border-gray-600 rounded p-2 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Minimum Stock */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Minimum Stock Level
                </label>
                <input
                  type="number"
                  value={formData.minimum_stock}
                  onChange={(e) =>
                    handleFormInputChange("minimum_stock", e.target.value)
                  }
                  placeholder="0"
                  className="w-full bg-gray-700 border border-gray-600 rounded p-2 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Reorder Level */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Reorder Level
                </label>
                <input
                  type="number"
                  value={formData.reorder_level}
                  onChange={(e) =>
                    handleFormInputChange("reorder_level", e.target.value)
                  }
                  placeholder="0"
                  className="w-full bg-gray-700 border border-gray-600 rounded p-2 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Status
                </label>
                <select
                  value={formData.is_active ? "active" : "inactive"}
                  onChange={(e) =>
                    handleFormInputChange(
                      "is_active",
                      e.target.value === "active",
                    )
                  }
                  className="w-full bg-gray-700 border border-gray-600 rounded p-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              {/* Description */}
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    handleFormInputChange("description", e.target.value)
                  }
                  placeholder="Product description..."
                  rows={4}
                  className="w-full bg-gray-700 border border-gray-600 rounded p-2 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="bg-gray-750 border-t border-gray-700 px-6 py-4 flex justify-end gap-2">
            <button
              onClick={() => {
                setIsProductFormOpen(false);
                setEditingProduct(null);
              }}
              disabled={isSaving}
              className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveProduct}
              disabled={isSaving}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSaving && (
                <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              )}
              {editingProduct ? "Update Product" : "Create Product"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  /**
   * Enhanced Stock Columns with all fields
   */
  const stockColumns: Column<Stock>[] = useMemo(
    () => [
      {
        key: "product",
        header: "Product",
        accessor: (row) => row.product?.name || "",
        sortable: true,
        filterable: true,
      },
      {
        key: "warehouse",
        header: "Location",
        accessor: (row) => row.warehouse?.name || "",
        sortable: true,
        filterable: true,
      },
      {
        key: "quantity",
        header: "On Hand",
        accessor: (row) => row.quantity,
        align: "right",
        sortable: true,
        className: "text-white font-semibold",
      },
      {
        key: "reserved_quantity",
        header: "Reserved",
        accessor: (row) => row.reserved_quantity || 0,
        align: "right",
        className: "text-yellow-400",
      },
      {
        key: "allocated_quantity",
        header: "Allocated",
        accessor: (row) => row.allocated_quantity || 0,
        align: "right",
        className: "text-orange-400",
      },
      {
        key: "available",
        header: "Available",
        accessor: (row) =>
          (row.quantity || 0) -
          (row.reserved_quantity || 0) -
          (row.allocated_quantity || 0),
        align: "right",
        className: "text-green-400 font-semibold",
      },
      {
        key: "last_counted_date",
        header: "Last Counted",
        accessor: (row) => row.last_counted_date,
        render: (value) => (value ? new Date(value).toLocaleDateString() : "-"),
      },
    ],
    [],
  );

  /**
   * Lot tracking with expiry
   */
  const lotColumns: Column<LotNumber>[] = useMemo(
    () => [
      {
        key: "lot_number",
        header: "Lot Number",
        accessor: (row) => (row as any).lot_number || "-",
        sortable: true,
        filterable: true,
        className: "text-white font-semibold",
      },
      {
        key: "product",
        header: "Product",
        accessor: (row) => row.product?.name || "",
        sortable: true,
        filterable: true,
      },
      {
        key: "quantity",
        header: "Quantity",
        accessor: (row) => row.quantity,
        align: "right",
        sortable: true,
      },
      {
        key: "manufacturing_date",
        header: "Mfg Date",
        accessor: (row) => row.manufacturing_date,
        render: (value) => (value ? new Date(value).toLocaleDateString() : "-"),
      },
      {
        key: "expiry_date",
        header: "Expiry Date",
        accessor: (row) => row.expiry_date,
        render: (value) => {
          if (!value) return "-";
          const expiry = new Date(value as string);
          const today = new Date();
          const daysUntilExpiry = Math.floor(
            (expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
          );

          return (
            <span
              className={`${
                daysUntilExpiry < 0
                  ? "text-red-400 font-bold"
                  : daysUntilExpiry <= 7
                    ? "text-orange-400 font-semibold"
                    : daysUntilExpiry <= 30
                      ? "text-yellow-400"
                      : "text-gray-300"
              }`}
            >
              {expiry.toLocaleDateString()}
              {daysUntilExpiry < 0 && " (EXPIRED)"}
              {daysUntilExpiry >= 0 &&
                daysUntilExpiry <= 30 &&
                ` (${daysUntilExpiry}d)`}
            </span>
          );
        },
        sortable: true,
      },
      {
        key: "warehouse",
        header: "Location",
        accessor: (row) => row.warehouse?.name || "-",
      },
      {
        key: "is_active",
        header: "Status",
        accessor: (row) => row.is_active,
        align: "center",
        render: (value) => (
          <span
            className={`px-2 py-1 text-xs rounded-full ${
              value
                ? "bg-green-900 text-green-200"
                : "bg-gray-700 text-gray-400"
            }`}
          >
            {value ? "Active" : "Inactive"}
          </span>
        ),
      },
    ],
    [],
  );

  // Serial Number Columns
  const serialNumberColumns: Column<SerialNumber>[] = useMemo(
    () => [
      {
        key: "serial_number",
        header: "Serial Number",
        accessor: (row) => row.serial_number,
        sortable: true,
        filterable: true,
        className: "text-white font-semibold",
      },
      {
        key: "product",
        header: "Product",
        accessor: (row) => row.product?.name || "",
        sortable: true,
        filterable: true,
      },
      {
        key: "lot_number",
        header: "Lot Number",
        accessor: (row) => row.lot_number || "-",
        sortable: true,
        filterable: true,
      },
      {
        key: "warehouse",
        header: "Location",
        accessor: (row) => row.warehouse?.name || "-",
      },
      {
        key: "manufactured_date",
        header: "Mfg Date",
        accessor: (row) => row.manufactured_date,
        render: (value) =>
          value ? new Date(value as string).toLocaleDateString() : "-",
      },
      {
        key: "expiry_date",
        header: "Expiry Date",
        accessor: (row) => row.expiry_date,
        render: (value) => {
          if (!value) return "-";
          const expiry = new Date(value as string);
          const today = new Date();
          const daysUntilExpiry = Math.floor(
            (expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
          );

          return (
            <span
              className={`${
                daysUntilExpiry < 0
                  ? "text-red-400 font-bold"
                  : daysUntilExpiry <= 7
                    ? "text-orange-400 font-semibold"
                    : daysUntilExpiry <= 30
                      ? "text-yellow-400"
                      : "text-gray-300"
              }`}
            >
              {expiry.toLocaleDateString()}
              {daysUntilExpiry < 0 && " (EXPIRED)"}
              {daysUntilExpiry >= 0 &&
                daysUntilExpiry <= 30 &&
                ` (${daysUntilExpiry}d)`}
            </span>
          );
        },
        sortable: true,
      },
      {
        key: "is_active",
        header: "Status",
        accessor: (row) => row.is_active,
        align: "center",
        render: (value) => (
          <span
            className={`px-2 py-1 text-xs rounded-full ${
              value
                ? "bg-green-900 text-green-200"
                : "bg-gray-700 text-gray-400"
            }`}
          >
            {value ? "Active" : "Inactive"}
          </span>
        ),
      },
    ],
    [],
  );


  const renderContent = () => {
    switch (activeTab) {
      case "products":
        return renderProductsContent();
      case "stock":
        return renderStockContent();
      case "warehouses":
        return renderWarehouseContent();
      case "suppliers":
        return renderSupplierContent();
      case "purchase-orders":
        return renderPurchaseOrderContent();
      case "analytics":
        return renderAnalyticsContent();
      case "transfers":
        return renderTransferContent();
      case "adjustments":
        return renderAdjustmentContent();
      case "transactions":
        return renderTransactionContent();
      case "traceability":
        return renderTraceabilityContent();
      default:
        return null;
    }
  };

  /**
   * Products rendering with DataTable
   */
  const renderProductsContent = () => {
    const subTabs = [
      { id: "master", label: "Master Data", icon: Package },
      { id: "variants", label: "Variants", icon: Layers },
      { id: "bundles", label: "Bundles", icon: Box },
      { id: "categories", label: "Categories", icon: Tag },
      { id: "pricing", label: "Pricing", icon: DollarSign },
      { id: "attributes", label: "Attributes", icon: Settings },
    ];

    return (
      <div className="space-y-4">
        {/* Sub Tabs */}
        <div className="flex gap-2 border-b border-gray-700 overflow-x-auto">
          {subTabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveSubTab(tab.id);
                  setCurrentPage(1);
                  setSearchTerm("");
                  setFilterOptions({ category: "", status: "" });
                }}
                className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors whitespace-nowrap ${
                  activeSubTab === tab.id
                    ? "border-blue-500 text-blue-400"
                    : "border-transparent text-gray-400 hover:text-gray-300"
                }`}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Master Products */}
        {activeSubTab === "master" && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <div className="flex gap-2">
                <div className="relative">
                  <Search
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                    size={18}
                  />
                  <input
                    type="text"
                    placeholder="Search products..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                    data-testid="input-search-products"
                    className="pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setIsExportDialogOpen(true)}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2"
                >
                  <Download size={18} />
                  Export
                </button>
                <button
                  onClick={handleNewProduct}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                  <Plus size={18} />
                  New Product
                </button>
              </div>
            </div>

            <ExportDialog
              isOpen={isExportDialogOpen}
              onClose={() => setIsExportDialogOpen(false)}
              entityType="products"
              data={products}
              availableFields={[
                "id",
                "name",
                "code",
                "barcode",
                "price",
                "cost_price",
                "category_id",
                "is_active",
                "unit_of_measure",
                "track_inventory",
              ]}
            />

            <DataTable
              data={products}
              columns={productColumns}
              loading={loading}
              error={error}
              pageSize={ITEMS_PER_PAGE}
              totalCount={totalCount}
              onPageChange={setCurrentPage}
              onFilter={handleFilterChange}
              rowKey={(row) => row.id}
              actions={(row) => (
                <div className="flex items-center justify-center gap-2">
                  <button
                    className="p-1 hover:bg-gray-700 rounded transition-colors"
                    title="View"
                    onClick={() => handleViewProduct(row)}
                  >
                    <Eye size={16} className="text-blue-400" />
                  </button>
                  <button
                    className="p-1 hover:bg-gray-700 rounded transition-colors"
                    title="Edit"
                    onClick={() => handleEditProduct(row)}
                  >
                    <Edit size={16} className="text-yellow-400" />
                  </button>
                  <button
                    className="p-1 hover:bg-gray-700 rounded transition-colors"
                    title="Delete"
                    onClick={() => openConfirmDialog({
                      title: "Delete Product",
                      message: `Are you sure you want to delete "${row.name}"?`,
                      onConfirm: async () => {
                        try {
                          await apiClient.delete(`/api/inventory/products/${row.id}`);
                          toast.success("Product deleted successfully");
                          fetchData();
                        } catch (error) {
                          toast.error("Failed to delete product");
                        }
                      }
                    })}
                  >
                    <Trash2 size={16} className="text-red-400" />
                  </button>
                </div>
              )}
              emptyState={
                <div className="bg-gray-800 rounded-lg p-12 text-center">
                  <Package size={48} className="mx-auto text-gray-600 mb-4" />
                  <div className="text-gray-400 text-lg mb-2">
                    No products found
                  </div>
                  <div className="text-gray-500 text-sm mb-4">
                    Get started by adding your first product
                  </div>
                  <button
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    onClick={handleNewProduct}
                  >
                    <Plus size={18} className="inline mr-2" />
                    Add Product
                  </button>
                </div>
              }
            />
          </div>
        )}

        {activeSubTab === "variants" && (
          <div className="space-y-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-white">
                Product Variants
              </h3>
              <button
                onClick={handleNewVariant}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                data-testid="button-new-variant"
              >
                <Plus size={18} />
                New Variant
              </button>
            </div>
            <DataTable
              data={variants}
              columns={variantColumns}
              loading={loading}
              error={error}
              pageSize={ITEMS_PER_PAGE}
              totalCount={totalCount}
              onPageChange={setCurrentPage}
              rowKey={(row) => row.id}
              actions={(row) => (
                <div className="flex gap-2">
                  <button
                    className="p-1 hover:bg-gray-700 rounded transition-colors"
                    onClick={() => {
                      setEditingVariant(row);
                      setVariantFormData({
                        product_id: row.product_id || "",
                        name: row.name || "",
                        sku: row.sku || "",
                        sale_price: row.sale_price?.toString() || "",
                        cost_price: row.cost_price?.toString() || "",
                        barcode: row.barcode || "",
                        is_active: row.is_active !== false,
                        sort_order: row.sort_order || 0,
                      });
                      setIsVariantFormOpen(true);
                    }}
                  >
                    <Edit size={16} className="text-yellow-400" />
                  </button>
                  <button
                    className="p-1 hover:bg-gray-700 rounded transition-colors"
                    onClick={() => openConfirmDialog({
                      title: "Delete Variant",
                      message: `Are you sure you want to delete variant "${row.name}"?`,
                      onConfirm: async () => {
                        try {
                          await apiClient.delete(`/api/inventory/variants/${row.id}`);
                          toast.success("Variant deleted successfully");
                          fetchData();
                        } catch (error) {
                          toast.error("Failed to delete variant");
                        }
                      }
                    })}
                  >
                    <Trash2 size={16} className="text-red-400" />
                  </button>
                </div>
              )}
            />
          </div>
        )}

        {activeSubTab === "bundles" && (
          <div className="space-y-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-white">
                Product Bundles
              </h3>
              <button
                onClick={handleNewBundle}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                data-testid="button-new-bundle"
              >
                <Plus size={18} />
                New Bundle
              </button>
            </div>
            <DataTable
              data={bundles}
              columns={bundleColumns}
              rowKey={(row: any) => row.id}
              actions={(row: any) => (
                <div className="flex gap-2">
                  <button
                    className="p-1 hover:bg-gray-700 rounded transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditBundle(row);
                    }}
                    data-testid={`button-edit-bundle-${row.id}`}
                  >
                    <Edit size={16} className="text-yellow-400" />
                  </button>
                  <button
                    className="p-1 hover:bg-gray-700 rounded transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      openConfirmDialog({
                        title: "Delete Bundle",
                        message: `Are you sure you want to delete bundle "${row.name}"?`,
                        onConfirm: async () => {
                          try {
                            await apiClient.delete(`/api/inventory/bundles/${row.id}`);
                            toast.success("Bundle deleted successfully");
                            fetchData();
                          } catch (error) {
                            toast.error("Failed to delete bundle");
                          }
                        }
                      });
                    }}
                    data-testid={`button-delete-bundle-${row.id}`}
                  >
                    <Trash2 size={16} className="text-red-400" />
                  </button>
                </div>
              )}
            />
          </div>
        )}

        {activeSubTab === "categories" && (
          <div className="space-y-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-white">Categories</h3>
              <button
                onClick={handleNewCategory}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                data-testid="button-new-category"
              >
                <Plus size={18} />
                New Category
              </button>
            </div>
            <DataTable
              data={categories}
              columns={categoryColumns}
              rowKey={(row: any) => row.id}
              actions={(row: any) => (
                <div className="flex gap-2">
                  <button
                    className="p-1 hover:bg-gray-700 rounded transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditCategory(row);
                    }}
                    data-testid={`button-edit-category-${row.id}`}
                  >
                    <Edit size={16} className="text-yellow-400" />
                  </button>
                  <button
                    className="p-1 hover:bg-gray-700 rounded transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      openConfirmDialog({
                        title: "Delete Category",
                        message: `Are you sure you want to delete category "${row.name}"?`,
                        onConfirm: async () => {
                          try {
                            await apiClient.delete(`/api/inventory/categories/${row.id}`);
                            toast.success("Category deleted successfully");
                            fetchData();
                          } catch (error) {
                            toast.error("Failed to delete category");
                          }
                        }
                      });
                    }}
                    data-testid={`button-delete-category-${row.id}`}
                  >
                    <Trash2 size={16} className="text-red-400" />
                  </button>
                </div>
              )}
            />
          </div>
        )}

        {activeSubTab === "pricing" && (
          <div className="space-y-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-white">
                Price Levels & Multi-Tier Pricing
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {Array.isArray(priceLevels) && priceLevels.map((level) => (
                <div
                  key={level.id}
                  className="bg-gray-800 rounded-lg p-4 border border-gray-700"
                >
                  <h4 className="text-white font-medium mb-2">{level.name}</h4>
                  <p className="text-sm text-gray-400">{level.description}</p>
                  <div className="mt-3 flex items-center justify-between">
                    <span className="text-xs text-gray-500">
                      Discount: {level.discount_percentage}%
                    </span>
                    <span
                      className={`px-2 py-1 text-xs rounded-full ${level.is_active ? "bg-green-900 text-green-200" : "bg-gray-700 text-gray-400"}`}
                    >
                      {level.is_active ? "Active" : "Inactive"}
                    </span>
                  </div>
                </div>
              ))}
              {(!Array.isArray(priceLevels) || priceLevels.length === 0) && (
                <div className="col-span-3 text-center text-gray-500 py-8">
                  No price levels configured
                </div>
              )}
            </div>

            <div className="bg-gray-800 rounded-lg p-4 mb-6">
              <h4 className="text-white font-medium mb-4">
                Promotional Pricing (Active)
              </h4>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-700">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">
                        Product
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">
                        Promo Price
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">
                        Start Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">
                        End Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">
                        Discount
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {Array.isArray(promotionalPricing) && promotionalPricing.map((promo) => (
                      <tr key={promo.id} className="hover:bg-gray-750">
                        <td className="px-4 py-3 text-sm text-white">
                          {promo.product?.name}
                        </td>
                        <td className="px-4 py-3 text-sm text-green-400">
                          $
                          {promo.promotional_price
                            ? parseFloat(
                                String(promo.promotional_price),
                              ).toFixed(2)
                            : "0.00"}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-400">
                          {new Date(promo.start_date).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-400">
                          {new Date(promo.end_date).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-yellow-400">
                          {promo.discount_percentage}%
                        </td>
                      </tr>
                    ))}
                    {promotionalPricing.length === 0 && (
                      <tr>
                        <td
                          colSpan={5}
                          className="px-4 py-8 text-center text-gray-500"
                        >
                          No active promotional pricing
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-gray-800 rounded-lg p-4">
              <h4 className="text-white font-medium mb-4">
                Price Change History
              </h4>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-700">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">
                        Date/Time
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">
                        Product
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">
                        Old Price
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">
                        New Price
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">
                        Changed By
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">
                        Reason
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {Array.isArray(priceChangesLog) && priceChangesLog.slice(0, 10).map((log) => (
                      <tr key={log.id} className="hover:bg-gray-750">
                        <td className="px-4 py-3 text-sm text-gray-400">
                          {new Date(log.changed_at).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-white">
                          {log.product?.name}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-400">
                          ${parseFloat(log.old_price).toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-sm text-green-400">
                          ${parseFloat(log.new_price).toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-400">
                          {log.changed_by?.first_name}{" "}
                          {log.changed_by?.last_name}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-400">
                          {log.reason || "-"}
                        </td>
                      </tr>
                    ))}
                    {priceChangesLog.length === 0 && (
                      <tr>
                        <td
                          colSpan={6}
                          className="px-4 py-8 text-center text-gray-500"
                        >
                          No price change history
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeSubTab === "attributes" && (
          <div className="space-y-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-white">
                Attributes & Specifications
              </h3>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-gray-800 rounded-lg p-4">
                <h4 className="text-white font-medium mb-4 flex items-center justify-between">
                  Variant Attributes
                </h4>
                <div className="space-y-3">
                  {Array.isArray(variantAttributes) && variantAttributes.map((attr) => (
                    <div key={attr.id} className="bg-gray-900 rounded p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-white font-medium">
                          {attr.display_name || attr.name}
                        </span>
                        <span className="text-xs text-gray-500">
                          Type: {attr.attribute_type}
                        </span>
                      </div>
                    </div>
                  ))}
                  {(!Array.isArray(variantAttributes) || variantAttributes.length === 0) && (
                    <p className="text-center text-gray-500 py-8">
                      No variant attributes defined
                    </p>
                  )}
                </div>
              </div>

              <div className="bg-gray-800 rounded-lg p-4">
                <h4 className="text-white font-medium mb-4">Manufacturers</h4>
                <div className="space-y-2">
                  {Array.isArray(manufacturers) && manufacturers.map((mfr) => (
                    <div
                      key={mfr.id}
                      className="bg-gray-900 rounded p-3 flex items-center justify-between"
                    >
                      <div>
                        <div className="text-white font-medium">{mfr.name}</div>
                        <div className="text-xs text-gray-500">{mfr.code}</div>
                      </div>
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${mfr.is_active ? "bg-green-900 text-green-200" : "bg-gray-700 text-gray-400"}`}
                      >
                        {mfr.is_active ? "Active" : "Inactive"}
                      </span>
                    </div>
                  ))}
                  {(!Array.isArray(manufacturers) || manufacturers.length === 0) && (
                    <p className="text-center text-gray-500 py-8">
                      No manufacturers configured
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-gray-800 rounded-lg p-4">
              <h4 className="text-white font-medium mb-4">Recipes</h4>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-700">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">
                        Product
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">
                        Ingredients
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">
                        Yield
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">
                        Prep Time
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-300">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {Array.isArray(recipes) && recipes.map((recipe) => (
                      <tr key={recipe.id} className="hover:bg-gray-750">
                        <td className="px-4 py-3 text-sm text-white">
                          {recipe.product?.name}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-400">
                          {recipe.items?.length || 0} items
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-400">
                          {recipe.yield_quantity} {recipe.yield_unit}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-400">
                          {recipe.prep_time_minutes} min
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span
                            className={`px-2 py-1 text-xs rounded-full ${recipe.is_active ? "bg-green-900 text-green-200" : "bg-gray-700 text-gray-400"}`}
                          >
                            {recipe.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>
                      </tr>
                    ))}
                    {recipes.length === 0 && (
                      <tr>
                        <td
                          colSpan={5}
                          className="px-4 py-8 text-center text-gray-500"
                        >
                          No recipes configured
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-gray-800 rounded-lg p-4">
              <h4 className="text-white font-medium mb-4">
                Modifier Groups (Restaurant)
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Array.isArray(modifierGroups) && modifierGroups.map((group) => (
                  <div
                    key={group.id}
                    className="bg-gray-900 rounded p-4 border border-gray-700"
                  >
                    <h5 className="text-white font-medium mb-2">
                      {group.name}
                    </h5>
                    <div className="text-xs text-gray-400 space-y-1">
                      <div>Min selections: {group.min_selections}</div>
                      <div>
                        Max selections: {group.max_selections || "Unlimited"}
                      </div>
                      <div>Options: {group.options?.length || 0}</div>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-1">
                      {group.options?.slice(0, 3).map((opt: any) => (
                        <span
                          key={opt.id}
                          className="px-2 py-1 bg-gray-800 text-gray-300 text-xs rounded"
                        >
                          {opt.name}
                        </span>
                      ))}
                      {(group.options?.length || 0) > 3 && (
                        <span className="px-2 py-1 bg-gray-800 text-gray-500 text-xs rounded">
                          +{(group.options?.length || 0) - 3} more
                        </span>
                      )}
                    </div>
                  </div>
                ))}
                {modifierGroups.length === 0 && (
                  <div className="col-span-full text-center text-gray-500 py-8">
                    No modifier groups configured
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  /**
   * Stock rendering with enhanced columns
   */
  const renderStockContent = () => {
    const subTabs = [
      { id: "levels", label: "Stock Levels", icon: Package },
      { id: "alerts", label: "Low Stock Alerts", icon: AlertTriangle },
      { id: "movements", label: "Movements", icon: ArrowUpDown },
    ];

    return (
      <div className="space-y-4">
        <div className="flex gap-2 border-b border-gray-700 overflow-x-auto">
          {subTabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveSubTab(tab.id);
                  setCurrentPage(1);
                }}
                className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors whitespace-nowrap ${
                  activeSubTab === tab.id
                    ? "border-blue-500 text-blue-400"
                    : "border-transparent text-gray-400 hover:text-gray-300"
                }`}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {activeSubTab === "levels" && (
          <DataTable
            data={stock}
            columns={stockColumns}
            loading={loading}
            error={error}
            pageSize={ITEMS_PER_PAGE}
            totalCount={totalCount}
            onPageChange={setCurrentPage}
            rowKey={(row) => row.id}
            emptyState={
              <div className="bg-gray-800 rounded-lg p-12 text-center">
                <Box size={48} className="mx-auto text-gray-600 mb-4" />
                <div className="text-gray-400 text-lg">
                  No stock data available
                </div>
              </div>
            }
          />
        )}

        {activeSubTab === "alerts" && (
          <div className="space-y-4">
            {stockAlerts.length === 0 && !loading ? (
              <div className="bg-gray-800 rounded-lg p-12 text-center">
                <CheckCircle
                  size={48}
                  className="mx-auto text-green-500 mb-4"
                />
                <div className="text-gray-400 text-lg">
                  All stock levels are healthy
                </div>
                <div className="text-gray-500 text-sm mt-2">
                  No low stock alerts at this time
                </div>
              </div>
            ) : (
              Array.isArray(stockAlerts) && stockAlerts.map((alert) => (
                <div
                  key={alert.id}
                  className="flex items-center justify-between p-4 bg-gray-700 rounded-lg border-l-4 border-red-500"
                >
                  <div className="flex items-center gap-4">
                    <AlertTriangle className="text-red-400" size={24} />
                    <div>
                      <div className="text-white font-medium">
                        {alert.product?.name}
                      </div>
                      <div className="text-sm text-gray-400">
                        Current: {alert.current_value} | Threshold:{" "}
                        {alert.threshold_value}
                      </div>
                    </div>
                  </div>
                  <button
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    onClick={() => toast.success("Reorder initiated")}
                  >
                    Reorder
                  </button>
                </div>
              ))
            )}
          </div>
        )}

        {activeSubTab === "movements" && (
          <div className="bg-gray-800 rounded-lg overflow-hidden">
            {Array.isArray(stockMovements) && stockMovements.length > 0 ? (
              <table className="w-full">
                <thead className="bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">
                      Product
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">
                      Type
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-300">
                      Quantity
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">
                      Reference
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {Array.isArray(stockMovements) && stockMovements.map((movement) => (
                    <tr key={movement.id} className="hover:bg-gray-750">
                      <td className="px-4 py-3 text-sm text-gray-400">
                        {new Date(movement.transaction_date).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-sm text-white">
                        {movement.product?.name}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-300">
                        {movement.transaction_type}
                      </td>
                      <td className="px-4 py-3 text-sm text-right">
                        <span
                          className={
                            parseFloat(movement.quantity) < 0
                              ? "text-red-400"
                              : "text-green-400"
                          }
                        >
                          {parseFloat(movement.quantity) > 0 ? "+" : ""}
                          {movement.quantity}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-400">
                        {movement.reference_id || "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="p-12 text-center text-gray-500">
                No stock movements recorded
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderWarehouseContent = () => {
    const subTabs = [
      { id: "locations", label: "Locations", icon: MapPin },
      { id: "zones", label: "Zones", icon: Box },
      { id: "stock", label: "Stock", icon: Package },
      { id: "capacity", label: "Capacity", icon: BarChart3 },
    ];

    return (
      <div className="space-y-4">
        <div className="flex gap-2 border-b border-gray-700 overflow-x-auto">
          {subTabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveSubTab(tab.id);
                  setCurrentPage(1);
                }}
                className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors whitespace-nowrap ${
                  activeSubTab === tab.id
                    ? "border-blue-500 text-blue-400"
                    : "border-transparent text-gray-400 hover:text-gray-300"
                }`}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {activeSubTab === "locations" && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Array.isArray(warehouses) && warehouses.map((wh) => (
              <div
                key={wh.id}
                className="bg-gray-800 rounded-lg p-6 border border-gray-700"
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-white">
                      {wh.name}
                    </h3>
                    <p className="text-sm text-gray-400">{wh.code}</p>
                  </div>
                  <WarehouseIcon className="text-blue-400" size={24} />
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Location:</span>
                    <span className="text-white">{wh.city}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Type:</span>
                    <span className="text-white">{wh.warehouse_type}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Status:</span>
                    <span
                      className={`px-2 py-1 rounded-full text-xs ${
                        wh.is_active
                          ? "bg-green-900 text-green-200"
                          : "bg-gray-700 text-gray-400"
                      }`}
                    >
                      {wh.is_active ? "Active" : "Inactive"}
                    </span>
                  </div>
                </div>
              </div>
            ))}

            {Array.isArray(stores) && stores.map((store) => (
              <div
                key={store.id}
                className="bg-gray-800 rounded-lg p-6 border border-gray-700"
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-white">
                      {store.name}
                    </h3>
                    <p className="text-sm text-gray-400">{store.code}</p>
                  </div>
                  <MapPin className="text-green-400" size={24} />
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">City:</span>
                    <span className="text-white">{store.city}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Status:</span>
                    <span
                      className={`px-2 py-1 rounded-full text-xs ${
                        store.is_active
                          ? "bg-green-900 text-green-200"
                          : "bg-gray-700 text-gray-400"
                      }`}
                    >
                      {store.is_active ? "Active" : "Inactive"}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeSubTab === "stock" && (
          <div className="bg-gray-800 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">
                    Product
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">
                    Warehouse
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-300">
                    Quantity
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-300">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {Array.isArray(warehouseStock) && warehouseStock.map((ws: any) => (
                  <tr key={ws.id} className="hover:bg-gray-750">
                    <td className="px-4 py-3 text-sm text-white">
                      {ws.product?.name || "-"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-400">
                      {ws.warehouse?.name || "-"}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-white">
                      {ws.quantity || 0}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${ws.is_active ? "bg-green-900 text-green-200" : "bg-gray-700 text-gray-400"}`}
                      >
                        {ws.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                  </tr>
                ))}
                {warehouseStock.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-12 text-center">
                      <Package
                        size={48}
                        className="mx-auto text-gray-600 mb-4"
                      />
                      <div className="text-gray-400 text-lg">
                        No warehouse stock data available
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  const renderSupplierContent = () => {
    const subTabs = [
      { id: "vendors", label: "Vendors", icon: Users },
      { id: "performance", label: "Performance", icon: TrendingUp },
      { id: "invoices", label: "Invoices", icon: FileText },
    ];

    return (
      <div className="space-y-4">
        <div className="flex gap-2 border-b border-gray-700 overflow-x-auto">
          {subTabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveSubTab(tab.id);
                  setCurrentPage(1);
                }}
                className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors whitespace-nowrap ${
                  activeSubTab === tab.id
                    ? "border-blue-500 text-blue-400"
                    : "border-transparent text-gray-400 hover:text-gray-300"
                }`}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {activeSubTab === "vendors" && (
          <div className="bg-gray-800 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">
                    Supplier
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">
                    Contact
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">
                    Payment Terms
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-300">
                    Credit Limit
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-300">
                    Status
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-300">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {Array.isArray(suppliers) && suppliers.map((supplier) => (
                  <tr key={supplier.id} className="hover:bg-gray-750">
                    <td className="px-4 py-3">
                      <div>
                        <div className="text-sm font-medium text-white">
                          {supplier.name}
                        </div>
                        <div className="text-xs text-gray-400">
                          {supplier.code}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-400">
                      {supplier.email}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-300">
                      {(supplier as any).payment_terms || "Net 30"}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-white">
                      $
                      {(supplier as any).credit_limit
                        ? parseFloat(
                            String((supplier as any).credit_limit),
                          ).toFixed(2)
                        : "0.00"}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${
                          supplier.is_active
                            ? "bg-green-900 text-green-200"
                            : "bg-gray-700 text-gray-400"
                        }`}
                      >
                        {supplier.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          className="p-1 hover:bg-gray-700 rounded"
                          onClick={() =>
                            openConfirmDialog({
                              title: "Edit Supplier",
                              message: `Are you sure you want to edit supplier ${supplier.name}?`,
                              onConfirm: () => console.log("Edit Supplier"),
                            })
                          }
                        >
                          <Edit size={16} className="text-yellow-400" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  const renderPurchaseOrderContent = () => {
    const subTabs = [
      { id: "active", label: "Active" },
      { id: "pending", label: "Pending Approval" },
      { id: "received", label: "Received" },
      { id: "history", label: "History" },
    ];

    return (
      <div className="space-y-4">
        <div className="flex gap-2 border-b border-gray-700 overflow-x-auto">
          {subTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveSubTab(tab.id);
                setCurrentPage(1);
              }}
              className={`px-4 py-2 border-b-2 transition-colors whitespace-nowrap ${
                activeSubTab === tab.id
                  ? "border-blue-500 text-blue-400"
                  : "border-transparent text-gray-400 hover:text-gray-300"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="bg-gray-800 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">
                  PO Number
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">
                  Supplier
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">
                  Date
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-300">
                  Total
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-300">
                  Status
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-300">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {Array.isArray(purchaseOrders) && purchaseOrders.map((po) => (
                <tr key={po.id} className="hover:bg-gray-750">
                  <td className="px-4 py-3 text-sm font-medium text-white">
                    {po.po_number}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-300">
                    {po.supplier?.name}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-400">
                    {new Date(po.order_date).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-white">
                    $
                    {po.total_amount
                      ? parseFloat(String(po.total_amount)).toFixed(2)
                      : "0.00"}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span
                      className={`px-2 py-1 text-xs rounded-full ${
                        po.status === "approved"
                          ? "bg-green-900 text-green-200"
                          : po.status === "pending"
                            ? "bg-yellow-900 text-yellow-200"
                            : "bg-gray-700 text-gray-400"
                      }`}
                    >
                      {po.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      className="p-1 hover:bg-gray-700 rounded"
                      onClick={() =>
                        openConfirmDialog({
                          title: "View PO",
                          message: `Are you sure you want to view PO ${po.po_number}?`,
                          onConfirm: () => console.log("View PO"),
                        })
                      }
                    >
                      <Eye size={16} className="text-blue-400" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderTransferContent = () => {
    const subTabs = [
      { id: "pending", label: "Pending" },
      { id: "in-transit", label: "In Transit" },
      { id: "completed", label: "Completed" },
      { id: "inter-warehouse", label: "Warehouse Transfers" },
    ];

    return (
      <div className="space-y-4">
        <div className="flex gap-2 border-b border-gray-700 overflow-x-auto">
          {subTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveSubTab(tab.id);
                setCurrentPage(1);
              }}
              className={`px-4 py-2 border-b-2 transition-colors whitespace-nowrap ${
                activeSubTab === tab.id
                  ? "border-blue-500 text-blue-400"
                  : "border-transparent text-gray-400 hover:text-gray-300"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="bg-gray-800 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">
                  Transfer #
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">
                  From
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">
                  To
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">
                  Date
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-300">
                  Status
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-300">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {(activeSubTab === "inter-warehouse"
                ? transferOrders
                : transfers
              ).map((transfer) => (
                <tr key={transfer.id} className="hover:bg-gray-750">
                  <td className="px-4 py-3 text-sm font-medium text-white">
                    {transfer.transfer_number}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-300">
                    {transfer.from_warehouse?.name || transfer.from_store?.name}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-300">
                    {transfer.to_warehouse?.name || transfer.to_store?.name}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-400">
                    {new Date(transfer.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span
                      className={`px-2 py-1 text-xs rounded-full ${
                        transfer.status === "completed"
                          ? "bg-green-900 text-green-200"
                          : transfer.status === "in_transit"
                            ? "bg-blue-900 text-blue-200"
                            : "bg-yellow-900 text-yellow-200"
                      }`}
                    >
                      {transfer.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      className="p-1 hover:bg-gray-700 rounded"
                      onClick={() =>
                        openConfirmDialog({
                          title: "View Transfer",
                          message: `Are you sure you want to view transfer ${transfer.transfer_number}?`,
                          onConfirm: () => console.log("View Transfer"),
                        })
                      }
                    >
                      <Eye size={16} className="text-blue-400" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderAdjustmentContent = () => {
    return (
      <div className="bg-gray-800 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-700">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">
                Adjustment #
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">
                Type
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">
                Reason
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">
                Date
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-300">
                Status
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-300">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {Array.isArray(adjustments) && adjustments.map((adj) => (
              <tr key={adj.id} className="hover:bg-gray-750">
                <td className="px-4 py-3 text-sm font-medium text-white">
                  {adj.adjustment_number}
                </td>
                <td className="px-4 py-3 text-sm text-gray-300">
                  {adj.adjustment_type}
                </td>
                <td className="px-4 py-3 text-sm text-gray-400">
                  {adj.reason}
                </td>
                <td className="px-4 py-3 text-sm text-gray-400">
                  {new Date(adj.adjustment_date).toLocaleDateString()}
                </td>
                <td className="px-4 py-3 text-center">
                  <span
                    className={`px-2 py-1 text-xs rounded-full ${
                      adj.status === "approved"
                        ? "bg-green-900 text-green-200"
                        : adj.status === "pending"
                          ? "bg-yellow-900 text-yellow-200"
                          : "bg-gray-700 text-gray-400"
                    }`}
                  >
                    {adj.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-center">
                  <button
                    className="p-1 hover:bg-gray-700 rounded"
                    onClick={() =>
                      openConfirmDialog({
                        title: "View Adjustment",
                        message: `Are you sure you want to view adjustment ${adj.adjustment_number}?`,
                        onConfirm: () => console.log("View Adjustment"),
                      })
                    }
                  >
                    <Eye size={16} className="text-blue-400" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderTransactionContent = () => {
    return (
      <div className="bg-gray-800 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-700">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">
                Date
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">
                Product
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">
                Type
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-300">
                Quantity
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">
                Reference
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-300">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {Array.isArray(transactions) && transactions.map((txn) => (
              <tr key={txn.id} className="hover:bg-gray-750">
                <td className="px-4 py-3 text-sm text-gray-400">
                  {new Date(txn.transaction_date).toLocaleString()}
                </td>
                <td className="px-4 py-3 text-sm text-white">
                  {txn.product?.name}
                </td>
                <td className="px-4 py-3 text-sm text-gray-300">
                  {txn.transaction_type}
                </td>
                <td className="px-4 py-3 text-sm text-right">
                  <span
                    className={
                      parseFloat(txn.quantity) < 0
                        ? "text-red-400"
                        : "text-green-400"
                    }
                  >
                    {parseFloat(txn.quantity) > 0 ? "+" : ""}
                    {txn.quantity}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-400">
                  {txn.reference_id || "-"}
                </td>
                <td className="px-4 py-3 text-center">
                  <button
                    className="p-1 hover:bg-gray-700 rounded"
                    onClick={() =>
                      openConfirmDialog({
                        title: "View Transaction",
                        message: `Are you sure you want to view transaction ${txn.id}?`,
                        onConfirm: () => console.log("View Transaction"),
                      })
                    }
                  >
                    <Eye size={16} className="text-blue-400" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  /**
   * Analytics with KPIs
   */
  const renderAnalyticsContent = () => {
    const analyticsData = analytics as any;
    const summary = analyticsData?.summary || {};
    const lowStockAlerts = analyticsData?.lowStockAlerts || [];
    const topMovingProducts = analyticsData?.topMovingProducts || [];
    const categoryBreakdown = analyticsData?.categoryBreakdown || [];

    return (
      <div className="space-y-6">
        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-gray-800 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-400">
                Total Inventory Value
              </h3>
              <DollarSign className="text-green-400" size={20} />
            </div>
            <div className="text-2xl font-bold text-white">
              ${summary.totalValue || "0.00"}
            </div>
          </div>

          <div className="bg-gray-800 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-400">
                Potential Profit
              </h3>
              <TrendingUp className="text-green-400" size={20} />
            </div>
            <div className="text-2xl font-bold text-green-400">
              ${summary.potentialProfit || "0.00"}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {summary.marginPercentage || 0}% margin
            </div>
          </div>

          <div className="bg-gray-800 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-400">
                Low Stock Items
              </h3>
              <AlertTriangle className="text-yellow-400" size={20} />
            </div>
            <div className="text-2xl font-bold text-yellow-400">
              {summary.lowStockItems || 0}
            </div>
          </div>

          <div className="bg-gray-800 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-400">
                Out of Stock
              </h3>
              <XCircle className="text-red-400" size={20} />
            </div>
            <div className="text-2xl font-bold text-red-400">
              {summary.outOfStockItems || 0}
            </div>
          </div>
        </div>

        {/* Category Breakdown */}
        {categoryBreakdown.length > 0 && (
          <div className="bg-gray-800 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-white mb-4">
              Inventory by Category
            </h3>
            <div className="space-y-3">
              {categoryBreakdown.slice(0, 10).map((cat: any) => (
                <div
                  key={cat.category_id}
                  className="flex items-center justify-between"
                >
                  <div className="flex-1">
                    <div className="text-white font-medium">
                      {cat.category_name}
                    </div>
                    <div className="text-sm text-gray-400">
                      {cat.product_count} products • $
                      {parseFloat(
                        cat.total_value_at_retail || 0,
                      ).toLocaleString()}{" "}
                      retail
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-white font-semibold">
                      $
                      {parseFloat(
                        cat.total_value_at_cost || 0,
                      ).toLocaleString()}
                    </div>
                    <div className="text-xs text-gray-500">cost</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Top Moving Products */}
        {topMovingProducts.length > 0 && (
          <div className="bg-gray-800 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-white mb-4">
              Top Moving Products (90 days)
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-700">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs text-gray-300">
                      Product
                    </th>
                    <th className="px-4 py-2 text-right text-xs text-gray-300">
                      Units Sold
                    </th>
                    <th className="px-4 py-2 text-right text-xs text-gray-300">
                      Revenue
                    </th>
                    <th className="px-4 py-2 text-right text-xs text-gray-300">
                      Avg Price
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {topMovingProducts.slice(0, 15).map((product: any) => (
                    <tr key={product.product_id} className="hover:bg-gray-750">
                      <td className="px-4 py-3 text-white">
                        {product.product_name}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-300">
                        {parseFloat(product.units_sold || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right text-green-400 font-semibold">
                        $
                        {parseFloat(
                          product.total_revenue || 0,
                        ).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-300">
                        $
                        {parseFloat(product.average_unit_price || 0).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Critical Alerts */}
        {lowStockAlerts.length > 0 && (
          <div className="bg-gray-800 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <AlertTriangle className="text-red-400" size={20} />
              Critical Stock Alerts
            </h3>
            <div className="space-y-2">
              {lowStockAlerts.slice(0, 10).map((alert: any) => (
                <div
                  key={alert.product_id}
                  className="flex items-center justify-between bg-red-900/20 border border-red-500/30 rounded p-3"
                >
                  <div>
                    <div className="text-white font-medium">
                      {alert.product_name}
                    </div>
                    <div className="text-sm text-gray-400">
                      {alert.alert_level} • Current: {alert.current_quantity} •
                      Min: {alert.minimum_stock}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-gray-400">Days remaining</div>
                    <div
                      className={`text-lg font-bold ${
                        parseFloat(alert.days_of_stock_remaining || 0) < 3
                          ? "text-red-400"
                          : parseFloat(alert.days_of_stock_remaining || 0) < 7
                            ? "text-yellow-400"
                            : "text-gray-300"
                      }`}
                    >
                      {alert.days_of_stock_remaining
                        ? parseFloat(alert.days_of_stock_remaining).toFixed(1)
                        : "N/A"}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  /**
   * Traceability with expiry tracking
   */
  const renderTraceabilityContent = () => {
    const subTabs = [
      { id: "lots", label: "Lot Numbers", icon: Tag },
      { id: "serials", label: "Serial Numbers", icon: FileText },
      { id: "expiry", label: "Expiry Tracking", icon: CalendarIcon },
    ];

    return (
      <div className="space-y-4">
        <div className="flex gap-2 border-b border-gray-700 overflow-x-auto">
          {subTabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveSubTab(tab.id);
                  setCurrentPage(1);
                }}
                className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors whitespace-nowrap ${
                  activeSubTab === tab.id
                    ? "border-blue-500 text-blue-400"
                    : "border-transparent text-gray-400 hover:text-gray-300"
                }`}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {(activeSubTab === "lots" || activeSubTab === "expiry") && (
          <DataTable
            data={activeSubTab === "expiry" ? expiringItems : lotNumbers}
            columns={lotColumns}
            loading={loading}
            error={error}
            pageSize={ITEMS_PER_PAGE}
            totalCount={totalCount}
            onPageChange={setCurrentPage}
            rowKey={(row) => row.id}
            emptyState={
              <div className="bg-gray-800 rounded-lg p-12 text-center">
                <Tag size={48} className="mx-auto text-gray-600 mb-4" />
                <div className="text-gray-400 text-lg">
                  {activeSubTab === "expiry"
                    ? "No expiring items"
                    : "No lot numbers tracked"}
                </div>
              </div>
            }
          />
        )}

        {activeSubTab === "serials" && (
          <DataTable
            data={serialNumbers}
            columns={serialNumberColumns}
            loading={loading}
            error={error}
            pageSize={ITEMS_PER_PAGE}
            totalCount={totalCount}
            onPageChange={setCurrentPage}
            rowKey={(row) => row.id}
            emptyState={
              <div className="bg-gray-800 rounded-lg p-12 text-center">
                <FileText size={48} className="mx-auto text-gray-600 mb-4" />
                <div className="text-gray-400 text-lg">
                  No serial numbers tracked
                </div>
              </div>
            }
          />
        )}
      </div>
    );
  };

  // Category Form Renderer
  const renderCategoryForm = () => {
    if (!isCategoryFormOpen) return null;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
        <div className="bg-gray-800 rounded-lg w-full max-w-2xl shadow-xl border border-gray-700">
          <div className="sticky top-0 bg-gray-800 border-b border-gray-700 px-6 py-4 flex justify-between items-center">
            <h3 className="text-xl font-bold text-white">
              {editingCategory ? "Edit Category" : "New Category"}
            </h3>
            <button
              onClick={() => {
                setIsCategoryFormOpen(false);
                setEditingCategory(null);
              }}
              className="text-gray-400 hover:text-white"
            >
              ✕
            </button>
          </div>
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Name *
              </label>
              <input
                type="text"
                value={categoryFormData.name}
                onChange={(e) =>
                  setCategoryFormData({
                    ...categoryFormData,
                    name: e.target.value,
                  })
                }
                className="w-full bg-gray-700 border border-gray-600 rounded p-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Color
              </label>
              <input
                type="text"
                value={categoryFormData.color}
                onChange={(e) =>
                  setCategoryFormData({
                    ...categoryFormData,
                    color: e.target.value,
                  })
                }
                placeholder="e.g., bg-blue-600"
                className="w-full bg-gray-700 border border-gray-600 rounded p-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Description
              </label>
              <textarea
                value={categoryFormData.description}
                onChange={(e) =>
                  setCategoryFormData({
                    ...categoryFormData,
                    description: e.target.value,
                  })
                }
                rows={3}
                className="w-full bg-gray-700 border border-gray-600 rounded p-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={categoryFormData.is_active}
                  onChange={(e) =>
                    setCategoryFormData({
                      ...categoryFormData,
                      is_active: e.target.checked,
                    })
                  }
                  className="mr-2"
                />{" "}
                <span className="text-gray-300">Active</span>
              </label>
            </div>
          </div>
          <div className="bg-gray-750 border-t border-gray-700 px-6 py-4 flex justify-end gap-2">
            <button
              onClick={() => {
                setIsCategoryFormOpen(false);
                setEditingCategory(null);
              }}
              disabled={isSaving}
              className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-500 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={async () => {
                setIsSaving(true);
                try {
                  if (editingCategory) {
                    await apiClient.put(`/api/inventory/categories/${editingCategory.id}`, categoryFormData);
                    toast.success("Category updated successfully");
                  } else {
                    await apiClient.post("/api/inventory/categories", categoryFormData);
                    toast.success("Category created successfully");
                  }
                  setIsCategoryFormOpen(false);
                  setEditingCategory(null);
                  fetchData();
                } catch (error: any) {
                  toast.error(error.response?.data?.error || "Failed to save category");
                } finally {
                  setIsSaving(false);
                }
              }}
              disabled={isSaving}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-500 disabled:opacity-50"
            >
              {isSaving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Variant Form Renderer
  const renderVariantForm = () => {
    if (!isVariantFormOpen) return null;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
        <div className="bg-gray-800 rounded-lg w-full max-w-2xl shadow-xl border border-gray-700">
          <div className="sticky top-0 bg-gray-800 border-b border-gray-700 px-6 py-4 flex justify-between items-center">
            <h3 className="text-xl font-bold text-white">
              {editingVariant ? "Edit Variant" : "New Variant"}
            </h3>
            <button
              onClick={() => {
                setIsVariantFormOpen(false);
                setEditingVariant(null);
              }}
              className="text-gray-400 hover:text-white"
            >
              ✕
            </button>
          </div>
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Product *
              </label>
              <select
                value={variantFormData.product_id}
                onChange={(e) =>
                  setVariantFormData({
                    ...variantFormData,
                    product_id: e.target.value,
                  })
                }
                className="w-full bg-gray-700 border border-gray-600 rounded p-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Product</option>
                {Array.isArray(products) && products.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Name *
              </label>
              <input
                type="text"
                value={variantFormData.name}
                onChange={(e) =>
                  setVariantFormData({
                    ...variantFormData,
                    name: e.target.value,
                  })
                }
                className="w-full bg-gray-700 border border-gray-600 rounded p-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                SKU
              </label>
              <input
                type="text"
                value={variantFormData.sku}
                onChange={(e) =>
                  setVariantFormData({
                    ...variantFormData,
                    sku: e.target.value,
                  })
                }
                className="w-full bg-gray-700 border border-gray-600 rounded p-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Price *
              </label>
              <input
                type="number"
                step="0.01"
                value={variantFormData.sale_price}
                onChange={(e) =>
                  setVariantFormData({
                    ...variantFormData,
                    sale_price: e.target.value,
                  })
                }
                className="w-full bg-gray-700 border border-gray-600 rounded p-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div className="bg-gray-750 border-t border-gray-700 px-6 py-4 flex justify-end gap-2">
            <button
              onClick={() => {
                setIsVariantFormOpen(false);
                setEditingVariant(null);
              }}
              disabled={isSaving}
              className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-500 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={async () => {
                setIsSaving(true);
                try {
                  const payload = {
                    ...variantFormData,
                    sale_price: parseFloat(variantFormData.sale_price || "0"),
                    cost_price: parseFloat(variantFormData.cost_price || "0"),
                  };

                  if (editingVariant) {
                    await apiClient.put(`/api/inventory/variants/${editingVariant.id}`, payload);
                    toast.success("Variant updated successfully");
                  } else {
                    await apiClient.post("/api/inventory/variants", payload);
                    toast.success("Variant created successfully");
                  }
                  setIsVariantFormOpen(false);
                  setEditingVariant(null);
                  fetchData();
                } catch (error: any) {
                  toast.error(error.response?.data?.error || "Failed to save variant");
                } finally {
                  setIsSaving(false);
                }
              }}
              disabled={isSaving}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-500 disabled:opacity-50"
            >
              {isSaving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Bundle Form Renderer
  const renderBundleForm = () => {
    if (!isBundleFormOpen) return null;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
        <div className="bg-gray-800 rounded-lg w-full max-w-2xl shadow-xl border border-gray-700">
          <div className="sticky top-0 bg-gray-800 border-b border-gray-700 px-6 py-4 flex justify-between items-center">
            <h3 className="text-xl font-bold text-white">
              {editingBundle ? "Edit Bundle" : "New Bundle"}
            </h3>
            <button
              onClick={() => {
                setIsBundleFormOpen(false);
                setEditingBundle(null);
              }}
              className="text-gray-400 hover:text-white"
            >
              ✕
            </button>
          </div>
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Name *
              </label>
              <input
                type="text"
                value={bundleFormData.name}
                onChange={(e) =>
                  setBundleFormData({ ...bundleFormData, name: e.target.value })
                }
                className="w-full bg-gray-700 border border-gray-600 rounded p-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Bundle Price *
              </label>
              <input
                type="number"
                step="0.01"
                value={bundleFormData.bundle_price}
                onChange={(e) =>
                  setBundleFormData({
                    ...bundleFormData,
                    bundle_price: e.target.value,
                  })
                }
                className="w-full bg-gray-700 border border-gray-600 rounded p-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Description
              </label>
              <textarea
                value={bundleFormData.description}
                onChange={(e) =>
                  setBundleFormData({
                    ...bundleFormData,
                    description: e.target.value,
                  })
                }
                rows={3}
                className="w-full bg-gray-700 border border-gray-600 rounded p-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div className="bg-gray-750 border-t border-gray-700 px-6 py-4 flex justify-end gap-2">
            <button
              onClick={() => {
                setIsBundleFormOpen(false);
                setEditingBundle(null);
              }}
              disabled={isSaving}
              className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-500 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={async () => {
                setIsSaving(true);
                try {
                  const payload = {
                    ...bundleFormData,
                    bundle_price: parseFloat(bundleFormData.bundle_price || "0"),
                    discount_percentage: parseFloat(bundleFormData.discount_percentage || "0"),
                  };

                  if (editingBundle) {
                    await apiClient.put(`/api/inventory/bundles/${editingBundle.id}`, payload);
                    toast.success("Bundle updated successfully");
                  } else {
                    await apiClient.post("/api/inventory/bundles", payload);
                    toast.success("Bundle created successfully");
                  }
                  setIsBundleFormOpen(false);
                  setEditingBundle(null);
                  fetchData();
                } catch (error: any) {
                  toast.error(error.response?.data?.error || "Failed to save bundle");
                } finally {
                  setIsSaving(false);
                }
              }}
              disabled={isSaving}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-500 disabled:opacity-50"
            >
              {isSaving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-gray-900">
        {/* Header */}
        <div className="bg-gray-800 px-6 py-3 flex items-center justify-between border-b border-gray-700">
          <div className="flex items-center gap-4">
            <Link
              to="/app"
              className="text-gray-400 hover:text-white transition-colors"
            >
              <ChevronLeft size={20} />
            </Link>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-600/20 rounded-lg">
                <Package size={22} className="text-blue-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Inventory Management</h2>
                <div className="text-sm text-gray-500">Manage products, stock levels, and supply chain</div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button
              className="p-2 hover:bg-gray-700 rounded"
              onClick={fetchData}
              title="Refresh"
            >
              <RefreshCw size={18} className="text-gray-400" />
            </button>
            <button
              onClick={handleMaximize}
              className="p-2 hover:bg-gray-700 rounded"
              title="Fullscreen"
            >
              <Maximize size={18} className="text-gray-400" />
            </button>
          </div>
        </div>

        {/* Main Tabs */}
        <div className="bg-gray-800 border-b border-gray-700">
          <div className="flex gap-1 px-6 overflow-x-auto">
            {[
              { id: "products", label: "Products", icon: Package, description: "Manage items" },
              { id: "stock", label: "Stock", icon: Box, description: "Current inventory" },
              { id: "warehouses", label: "Warehouses", icon: WarehouseIcon, description: "Storage locations" },
              { id: "suppliers", label: "Suppliers", icon: TruckIcon, description: "Vendor list" },
              { id: "purchase-orders", label: "Purchase Orders", icon: ShoppingCart, description: "Incoming stock" },
              { id: "transfers", label: "Transfers", icon: ArrowUpDown, description: "Moving stock" },
              { id: "adjustments", label: "Adjustments", icon: Settings, description: "Stock corrections" },
              { id: "transactions", label: "Transactions", icon: FileText, description: "Movement logs" },
              { id: "analytics", label: "Analytics", icon: BarChart3, description: "Stock insights" },
              { id: "traceability", label: "Traceability", icon: Tag, description: "Lots & serials" },
            ].map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id as TabType);
                    setActiveSubTab(
                      tab.id === "products"
                        ? "master"
                        : tab.id === "stock"
                          ? "levels"
                          : tab.id === "warehouses"
                            ? "locations"
                            : tab.id === "suppliers"
                              ? "vendors"
                              : tab.id === "purchase-orders"
                                ? "active"
                                : tab.id === "transfers"
                                  ? "pending"
                                  : tab.id === "adjustments"
                                    ? "pending"
                                    : tab.id === "transactions"
                                      ? "all"
                                      : tab.id === "analytics"
                                        ? "turnover"
                                        : "lots",
                    );
                    setCurrentPage(1);
                  }}
                  className={`group flex flex-col items-start px-4 py-3 border-b-2 transition-all whitespace-nowrap min-w-[120px] ${
                    isActive
                      ? "border-blue-500 text-blue-400 bg-gray-750"
                      : "border-transparent text-gray-400 hover:text-gray-300 hover:bg-gray-750/50"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Icon size={18} />
                    <span className="text-sm font-medium">{tab.label}</span>
                  </div>
                  <div className={`text-xs transition-all ${
                    isActive ? "text-blue-300/70" : "text-gray-500 group-hover:text-gray-400"
                  }`}>
                    {tab.description}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">{renderContent()}</div>
      </div>

      {renderViewProductModal()}
      {renderProductForm()}
      {renderCategoryForm()}
      {renderVariantForm()}
      {renderBundleForm()}

      <ConfirmDialog
        isOpen={showConfirmDialog}
        title={confirmDialogProps.title}
        message={confirmDialogProps.message}
        onConfirm={confirmAction}
        onClose={closeConfirmDialog}
      />
    </ErrorBoundary>
  );
}
